import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "@/store";
import {
  fetchClients,
  createClient,
  deleteClient,
} from "@/store/slices/clientSlice";
import { useNotification } from "@/context/NotificationContext";
import {
  PAGE_MAX_WIDTH,
  CURRENCY_OPTIONS,
  COUNTRY_CODE_LENGTH,
} from "@/constants";
import { PageLoading } from "@/components/common/PageLoading";
import { PageHeader } from "@/components/common/PageHeader";
import { ManagementTabs } from "@/components/common/ManagementTabs";
import { EmptyTableRow } from "@/components/common/EmptyTableRow";
import { TableHeaderCell } from "@/components/common/TableHeaderCell";

export function ClientsPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { list, loading } = useAppSelector((s) => s.clients);
  const { notify } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [country, setCountry] = useState("HR");
  const [currency, setCurrency] = useState("EUR");

  useEffect(() => {
    dispatch(fetchClients());
  }, [dispatch]);

  const handleCreate = async () => {
    await dispatch(
      createClient({
        name,
        description: description || undefined,
        country,
        currency,
      }),
    ).unwrap();
    notify(t("common.success"), "success");
    setName("");
    setDescription("");
    setCountry("HR");
    setCurrency("EUR");
    setDialogOpen(false);
  };

  if (loading) return <PageLoading />;

  return (
    <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: "auto" }}>
      <PageHeader
        title={t("projects.title")}
        subtitle={t("projects.subtitle")}
      />
      <ManagementTabs activeTab={1} />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setDialogOpen(true)}
        >
          {t("clients.addClient")}
        </Button>
      </Box>
      <Card variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableHeaderCell>{t("clients.clientName")}</TableHeaderCell>
                <TableHeaderCell>{t("common.description")}</TableHeaderCell>
                <TableHeaderCell>{t("clients.country")}</TableHeaderCell>
                <TableHeaderCell>{t("clients.currency")}</TableHeaderCell>
                <TableHeaderCell>{t("clients.linkedProjects")}</TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("common.actions")}
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((c: any) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Typography
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {c.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: "text.secondary",
                        maxWidth: 200,
                      }}
                    >
                      {c.description || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.country}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.currency}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {c.clientProjects?.length || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => dispatch(deleteClient(c.id))}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {list.length === 0 && <EmptyTableRow colSpan={6} />}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("clients.addClient")}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label={t("clients.clientName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            margin="dense"
          />
          <TextField
            label={t("common.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
            margin="dense"
          />
          <TextField
            label={t("clients.country")}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            fullWidth
            margin="dense"
            slotProps={{ htmlInput: { maxLength: COUNTRY_CODE_LENGTH } }}
            helperText={t("clients.countryHelp")}
          />
          <TextField
            label={t("clients.currency")}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            select
            fullWidth
            margin="dense"
          >
            {CURRENCY_OPTIONS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!name.trim() || country.length !== COUNTRY_CODE_LENGTH}
          >
            {t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
